import React, { useMemo } from "react";
import { Paper, Fade } from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useNavigate } from "react-router-dom";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";
import CommonButtons from "src/shared/components/CommonButtons";
import { usePaymentTheme } from "../hooks/usePaymentTheme";
import { OrderList } from "../../../orders/types/orderTypes";

interface Props {
  order: OrderList | null;
}

interface ProductRow {
  id: number;
  productName: string;
  amount: string | number;
  quantity: number;
  totalPrice: string;
}

const OrderDetailsSection: React.FC<Props> = ({ order }) => {
  const navigate = useNavigate();
  const { fontClass, getBgClass, themeTextColor, getLightBgClass } = usePaymentTheme();

  const rows: ProductRow[] = useMemo(() => {
    if (!order?.Products) return [];
    return order.Products.map((p, index) => ({
      id: index + 1,
      productName: p.productName || "Unknown Product",
      amount: (Number(p.amount || "0.00").toFixed(2)),
      quantity: p.quantity || 0,
      totalPrice: `${(Number(p.amount || 0) * (p.quantity || 0)).toFixed(2)}`,
    }));
  }, [order]);

  const headCells: HeadCell<ProductRow>[] = [
    { id: "id", label: "S.No" },
    { id: "productName", label: "ITEM" },
    { id: "amount", label: "PRICE", numeric: true, align: "right" },
    { id: "quantity", label: "QTY", numeric: true, align: "right" },
    { id: "totalPrice", label: "TOTAL", numeric: true, align: "right" },
  ];

  const status = order?.OrderStatus?.toLowerCase();
  const orderStatusColor =
    status === "completed"
      ? "text-green-700 bg-green-100 border border-green-300"
      : status === "pending"
      ? "text-yellow-700 bg-[#fff8cc] border border-[#f5da42]"
      : status === "cancelled"
      ? "text-red-700 bg-red-100 border border-red-300"
      : "text-gray-700 bg-gray-100 border border-gray-300";

  return (
    <Fade in={true} timeout={800}>
      <Paper className={`relative w-full bg-white rounded-2xl shadow-lg p-1.5 md:p-2 flex flex-col min-h-[92vh] ${fontClass}`}>
        <div className="flex items-center gap-3 flex-wrap text-sm md:text-base px-1">
          <CommonButtons 
            label="BACK" 
            className="h-8" 
            onClick={() => navigate(-1)} 
            variant="secondary" 
            icon={KeyboardReturnIcon} 
          />

          <div className={`flex items-center justify-center w-[34px] h-[34px] ${getBgClass()} rounded-xl shadow-md`}>
            <ReceiptIcon className="text-white text-[16px]" />
          </div>

          <h2 className={`font-bold text-lg ${themeTextColor}`}>Order #{order?.Orderid || "N/A"}</h2>

          <span className={`flex items-center gap-1.5 ${getLightBgClass()} text-gray-800 px-2 py-0.5 rounded-full font-medium`}>
            <LocalDiningIcon className={`text-sm ${themeTextColor}`} />
            Table: {order?.table_number || "N/A"}
          </span>

          <div className="ml-auto">
            {order?.OrderStatus && (
              <div className={`px-3 py-1.5 rounded-2xl border font-semibold text-xs md:text-sm tracking-wide whitespace-nowrap ${orderStatusColor}`}>
                {order.OrderStatus}
              </div>
            )}
          </div>
        </div>

        <div className="thin-scrollbar overflow-y-auto h-[605px] rounded-xl p-1">
          <CommonTable<ProductRow>
            data={rows}
            headCells={headCells}
            title=""
            selectable={false}
            denseToggle={false}
            showToolbar={false}
            pagenation={true}
            showFilterIcon={false}
            pageSize={10}
            className="w-full overflow-y-auto"
          />
        </div>
      </Paper>
    </Fade>
  );
};

export default OrderDetailsSection;